import useApi from "../../services/auth/useApi";
import { Operation, BlockRequestQueryTypeParam, BlockVisibility } from "../../types/block/enum";
import { BlockType } from "../../types/block/type";
import blockAPI from "../../api/BlockAPI";
import { getDayBoundariesISOString } from "../../utils/date-format/dateUtils";
import { convertServerToClientBlockType } from "../../utils/common/block/type-converter";
import {
  categorizeEventsByRecurrence,
  processInboxRecurringEvents,
} from "../../utils/calendar/initializeDataUtils";
import { extractLatestBlocksByOriginalId } from "./utils/extractLatestBlocksByOriginalId";
import { extractFirstBlocksByOriginalId } from "./utils/extractFirstBlocksByOriginalId";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { ResponseError } from "../../api/types";
import { usePreference } from "../../hooks/preference/usePreference";

/** queryKey와 queryFn을 제외한 쿼리 옵션 */
type InboxQueryOptions<TQueryFnData = unknown> = Omit<
  UseQueryOptions<TQueryFnData, ResponseError>,
  "queryKey" | "queryFn"
>;

// inbox param 정의
const TODAY = new Date();
const { START_OF_DAY, END_OF_DAY } = getDayBoundariesISOString({ day: TODAY, isAllday: true });

const INBOX_PARAM = {
  INBOX_GENERAL: {
    queryType: BlockRequestQueryTypeParam.INBOX,
    startOp: Operation.NOT,
    endOp: Operation.NOT,
  },
  INBOX_OVERDUE: {
    queryType: BlockRequestQueryTypeParam.TASK,
    end: START_OF_DAY,
    endOp: Operation.LESS_THAN_OR_EQUAL,
  },
  INBOX_COMPLETED: {
    queryType: BlockRequestQueryTypeParam.COMPLETED,
  },
  TODAY_GENERAL: {
    queryType: BlockRequestQueryTypeParam.TASK,
    start: END_OF_DAY,
    startOp: Operation.LESS_THAN,
    end: START_OF_DAY,
    endOp: Operation.GREATER_THAN,
  },
};

export const inboxQueryKeys = {
  all: () => ["inbox"] as const,
  inboxGeneral: () => [...inboxQueryKeys.all(), "general", INBOX_PARAM.INBOX_GENERAL] as const,
  inboxOverdue: () => [...inboxQueryKeys.all(), "overdue", INBOX_PARAM.INBOX_OVERDUE] as const,
  inboxCompleted: () =>
    [...inboxQueryKeys.all(), "completed", INBOX_PARAM.INBOX_COMPLETED] as const,
  todayGeneral: () =>
    [...inboxQueryKeys.all(), "today", "general", INBOX_PARAM.TODAY_GENERAL] as const,
} as const;

/**
 * @param {object | undefined} props
 * @param {boolean | undefined} props.enabled - 쿼리 실행 여부
 */
export const useInboxGeneralQuery = (options?: InboxQueryOptions<BlockType[]>) => {
  const api = useApi();
  const params = INBOX_PARAM.INBOX_GENERAL;

  const { preference: userPreference } = usePreference();
  const defaultVisibility = userPreference?.defaultVisibility ?? BlockVisibility.PUBLIC;

  return useQuery({
    queryKey: inboxQueryKeys.inboxGeneral(),
    queryFn: () =>
      blockAPI.getBlocks(api, params).then((res) => {
        return res.data.map((block) => {
          return convertServerToClientBlockType({
            serverBlock: block,
            defaultVisibility,
            wholeServerBlocks: res.data,
          });
        });
      }),
    ...options,
  });
};

export const useInboxOverdueQuery = (options?: InboxQueryOptions<BlockType[]>) => {
  const TODAY = new Date();
  const { START_OF_DAY } = getDayBoundariesISOString({ day: TODAY, isAllday: false });

  const api = useApi();
  const params = INBOX_PARAM.INBOX_OVERDUE;

  const { preference: userPreference } = usePreference();
  const defaultVisibility = userPreference?.defaultVisibility ?? BlockVisibility.PUBLIC;

  return useQuery({
    queryKey: inboxQueryKeys.inboxOverdue(),
    queryFn: () =>
      blockAPI.getBlocks(api, params).then((res) => {
        const clientData = res.data.map((block) => {
          return convertServerToClientBlockType({
            serverBlock: block,
            defaultVisibility,
            wholeServerBlocks: res.data,
          });
        });

        const categorizedEvents = categorizeEventsByRecurrence(clientData);
        const recurringStart = "";
        const recurringEnd = new Date(new Date(START_OF_DAY).getTime() - 1); // 오늘00시00분00초까지로 하면 오늘 블록이 포함되기 때문에 1초 전으로 설정

        const recurringEvents = processInboxRecurringEvents(
          categorizedEvents.recurrenceBlocks,
          categorizedEvents.recurrenceUpdatedBlocks,
          recurringStart,
          recurringEnd
        );

        const updatedBlocks = [...categorizedEvents.normalBlocks, ...recurringEvents];

        // start를 기준으로 오름차순
        const sortedBlocks = updatedBlocks.sort((a, b) => {
          return new Date(a.start).getTime() - new Date(b.start).getTime();
        });
        return sortedBlocks;
      }),
    select: (data: BlockType[]) =>
      data.filter((block) => {
        // 새로 생성된 블록은 필터링 적용 안함
        const isNewBlock = block.id === 0;
        if (isNewBlock) {
          return block;
        }
        return block.spaceId && userPreference?.selectedSpaceIds?.includes(block?.spaceId);
      }),
    ...options,
  });
};

export const useInboxCompletedQuery = (options?: InboxQueryOptions<BlockType[]>) => {
  const api = useApi();
  const params = INBOX_PARAM.INBOX_COMPLETED;

  const { preference: userPreference } = usePreference();
  const defaultVisibility = userPreference?.defaultVisibility ?? BlockVisibility.PUBLIC;

  return useQuery({
    queryKey: inboxQueryKeys.inboxCompleted(),
    queryFn: () =>
      blockAPI.getBlocks(api, params).then((res) => {
        const clientData = res.data.map((block) => {
          return convertServerToClientBlockType({
            serverBlock: block,
            defaultVisibility,
            wholeServerBlocks: res.data,
          });
        });
        return clientData;
      }),
    select: (data: BlockType[]) => {
      const recurrenceBlocks = extractLatestBlocksByOriginalId(data);
      const normalBlocks = data.filter((block) => !block.originalId);

      // spaceId 필터링
      const updatedBlocks = [...recurrenceBlocks, ...normalBlocks].filter((block) => {
        return block.spaceId && userPreference?.selectedSpaceIds?.includes(block?.spaceId);
      });

      // completedAt을 기준으로 내림차순 정렬
      const sortedBlocks = updatedBlocks.sort((a, b) => {
        // completedAt이 없는 경우 가장 뒤로 정렬
        if (!a.completedAt) return 1;
        if (!b.completedAt) return -1;
        return new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime();
      });

      return sortedBlocks;
    },
    ...options,
  });
};

export const useTodayGeneralQuery = (options?: InboxQueryOptions<BlockType[]>) => {
  const api = useApi();
  const params = INBOX_PARAM.TODAY_GENERAL;

  const { preference: userPreference } = usePreference();
  const defaultVisibility = userPreference?.defaultVisibility ?? BlockVisibility.PUBLIC;

  return useQuery({
    queryKey: inboxQueryKeys.todayGeneral(),
    queryFn: () =>
      blockAPI.getBlocks(api, params).then((res) => {
        const TODAY = new Date();
        const { START_OF_DAY, END_OF_DAY } = getDayBoundariesISOString({
          day: TODAY,
          isAllday: false,
        });

        const clientData = res.data.map((block) => {
          return convertServerToClientBlockType({
            serverBlock: block,
            defaultVisibility,
            // NOTE this로 수정된 블록이 있을 수 있기 때문에 필요
            wholeServerBlocks: res.data,
          });
        });

        const categorizedEvents = categorizeEventsByRecurrence(clientData);
        const recurringStart = START_OF_DAY;
        const recurringEnd = END_OF_DAY;
        const recurringEvents = processInboxRecurringEvents(
          categorizedEvents.recurrenceBlocks,
          categorizedEvents.recurrenceUpdatedBlocks,
          recurringStart,
          recurringEnd
        );

        const updatedBlocks = [...categorizedEvents.normalBlocks, ...recurringEvents];
        return updatedBlocks;
      }),
    select: (data: BlockType[]) => {
      const processedBlocks = extractFirstBlocksByOriginalId(data);

      // 3. 필터링 적용
      return processedBlocks.filter((block) => {
        // 새로 생성된 블록은 필터링 적용 안함
        const isNewBlock = block.id === 0;
        if (isNewBlock) {
          return block;
        }
        return block.spaceId && userPreference?.selectedSpaceIds?.includes(block.spaceId);
      });
    },
    ...options,
  });
};
